import React from 'react';
import _ from 'lodash';

import { styled } from '@mui/material/styles';
import { Link as RouterLink } from 'react-router-dom';
import { Observable } from 'rxjs';
import {
  Table as MuiTable,
  TableBody,
  TableRow as BaseTableRow,
  TableCell as BaseTableCell,
  TableHead,
  TableFooter,
  TablePagination as BaseTablePagination,
  Typography,
  Link,
  Box,
  Checkbox as MuiCheckbox,
  Collapse
} from '@mui/material';
import {
  ArrowDropDown,
  ArrowDropUp
} from '@mui/icons-material';

import {
  paginationUtil,
  sortUtil,
  RowBox
} from '../../layer0';

import * as RowPopover from './RowPopover/RowPopover';
import * as ExpandCollapseButton from './ExpandCollapseButton/ExpandCollapseButton';

export type { IPopoverOption } from './RowPopover/RowPopover';



interface IHeaderCellBoxProps {
  align?: 'left' | 'center' | 'right';
}

const HeaderCellBox = styled(Box, {
  shouldForwardProp: prop =>
    prop !== 'align'
})<IHeaderCellBoxProps>(({align}) => ({
  display: 'flex',
  justifyContent: align === 'center' ?
    'center' :
    (align === 'right' ?
      'flex-end' :
      undefined),
  alignItems: 'center'
}));

interface IHiddenBoxProps {
  isHidden: boolean;
}

const HiddenBox = styled(Box, {
  shouldForwardProp: prop =>
    prop !== 'isHidden'
})<IHiddenBoxProps>(({isHidden}) => ({
  ...(isHidden && {
    opacity: 0
  })
}));

const IconContainerBox = styled(Box)(({theme}) => ({
  marginLeft: theme.spacing(1),
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center'
}));

interface ITableRowProps {
  backgroundColor?: string;
}

const TableRow = styled(BaseTableRow, {
  shouldForwardProp: prop =>
    prop !== 'backgroundColor'
})<ITableRowProps>(({backgroundColor}) => ({
  backgroundColor
}));

const TableCell = styled(BaseTableCell)(({variant}) => ({
  paddingLeft: 0,
  borderColor: '#EFF5F8',
  borderBottomWidth: variant === 'head' ? 3 : 1
}));

const SelectAllHeaderTableCell = styled(TableCell)({
  width: '1px'
});

const SelectTableCell = styled(TableCell)({
  width: '1px'
});

const SelectionFooterTableCell = styled(TableCell)(() => ({
  border: 'none'
}));

const PopoverHeaderTableCell = styled(TableCell)({
  width: '1px'
});

const PopoverTableCell = styled(TableCell)({
  width: '1px',
  textAlign: 'right'
});

const ExpandedContentHeaderTableCell = styled(TableCell)({
  width: '1px'
});

const ExpandedContentTableCell = styled(TableCell)({
  width: '1px',
  paddingTop: 0,
  paddingBottom: 0
});

const HeaderCellTypography = styled(Typography)(() => ({
  fontSize: '12px',
  fontWeight: 600,
  color: '#6B788B'
}));

const CellTypography = styled(Typography)(() => ({
  fontSize: '12px',
  fontWeight: 400,
  color: '#6B788B'
}));

const TablePagination = styled(BaseTablePagination)(() => ({
  border: 'none'
}));



export interface IColumn<TColumnId extends string> {
  id: TColumnId;
  label: string;
  disableSorting?: boolean;
  sortType?: 'string' | 'number';
  width?: string;
  textAlign?: 'left' | 'center' | 'right';
  show?: boolean;
}

export interface IRowStringValue {
  type: 'string';
  sortableValue?: string | number;
  label: string;
  toRoute?: string;
  isExternalUrl?: boolean;
}

export interface IRowComponentValue {
  type: 'component';
  sortableValue: string | number;
  label: JSX.Element;
  toRoute?: string;
  isExternalUrl?: boolean;
  displayAsRow?: boolean;
}

export type IRowValue =
  IRowStringValue |
  IRowComponentValue;

export interface IRow<TColumnId extends string> {
  id: string;
  columnId2Value: Record<TColumnId, IRowValue>;
  popoverOptions?: RowPopover.IPopoverOption[];
  backgroundColor?: string;
  expandedContent?: JSX.Element;
}

/**
 * Convenience interface used to quickly generate column
 * and row types.
 */
export interface IInterfaces<TColumnId extends string> {
  IColumnId: TColumnId;
  IColumn: IColumn<TColumnId>;
  IRow: IRow<TColumnId>;
}

export interface IPaginationOptions {
  rowCount: number;
  rowsPerPage: number;
  page: number;
  onChangePage: (page: number) => void;
}

export function pageInfo2PaginationOptions(pageInfo: paginationUtil.IPageInfo, fnWrappedQueryPage: paginationUtil.FnWrappedQueryPage): IPaginationOptions {
  return {
    rowCount: pageInfo.totalRows,
    rowsPerPage: pageInfo.rowsPerPage,
    page: pageInfo.currentPage,
    onChangePage: page => fnWrappedQueryPage(page)
  };
}

export interface IMultiSelectOptions {
  popoverOptions?: RowPopover.IPopoverOption[];
  onClickPopoverOption?: (selectedRowIds: string[], optionId: string) => void;
  singleSelect?: boolean;

  /**
   * If you change the data for the table and you want to clear row selections,
   * send an event into this observable.
   */
  obsDeselectAll?: Observable<void>;

  /**
   * Pass a function in to receive updates about the set of selections whenever
   * selections change.
   */
  onChangeSelections?: (rowId2IsSelected: Record<string, boolean>) => void;
}

export interface ICheckboxProps {
  isChecked: boolean;
  onChange: (value: boolean) => void;
}

export interface IGenTableComponentOptions {
  tableStyle?: React.CSSProperties;
  tableBodyRowStyle?: React.CSSProperties;
  tableBodyCellStyle?: React.CSSProperties;
  tableHeaderRowStyle?: React.CSSProperties;
  tableHeaderCellStyle?: React.CSSProperties;
  cellTypographyStyle?: React.CSSProperties;
  headerCellTypographyStyle?: React.CSSProperties;

  elSortAscendingIcon?: JSX.Element;
  elSortDescendingIcon?: JSX.Element;

  fcCheckbox?: React.FC<ICheckboxProps>;
}

export interface ITableProps<TColumnId extends string> extends IGenTableComponentOptions {
  columns: IColumn<TColumnId>[];
  rows: IRow<TColumnId>[];
  multiSelectOptions?: IMultiSelectOptions;
  disableSorting?: boolean;
  disableClientSorting?: boolean;
  initSortOptions?: sortUtil.ISortOptions<TColumnId>;
  onChangeSortOptions?: (sortOptions: sortUtil.ISortOptions<TColumnId>) => void;
  onClickRowPopoverOption?: (rowId: string, optionId: string) => void;
  paginationOptions?: IPaginationOptions;
  expandButtonPosition?: 'left' | 'right';
}

export function genTableComponent<TColumnId extends string>(options?: IGenTableComponentOptions) {
  const Table: React.FC<ITableProps<TColumnId>> = props => {
    const [rowId2IsChecked, _setRowId2IsChecked] = React.useState<Record<string, boolean>>({});
    const [rowId2IsExpanded, setRowId2IsExpanded] = React.useState<Record<string, boolean>>({});

    const expandButtonPosition = props.expandButtonPosition ?? 'left';

    const tableHasAtLeastOneExpandableRow = React.useMemo(
      () => _.filter(props.rows, row => !!row.expandedContent).length > 0,
      [props]
    );

    const tableHasRowPopoverColumn = React.useMemo(
      () => {
        const allPopoverOptions = _.flatMap(props.rows, row => row.popoverOptions ?? []);
        const shownPopoverOptions = _.filter(allPopoverOptions, option => option.show !== false);
        return shownPopoverOptions.length > 0;
      },
      [props]
    );

    const numRowsChecked = React.useMemo(
      () => _.size(_.pickBy(rowId2IsChecked)),
      [rowId2IsChecked]
    );

    const areAllRowsSelected = React.useMemo(
      () => numRowsChecked === props.rows.length && props.rows.length > 0,
      [
        numRowsChecked,
        props.rows
      ]
    );

    const {columns} = props;
    const shownColumns = React.useMemo(
      () => _.filter(columns, column =>
        _.isUndefined(column.show) ||
        column.show),
      [columns]
    );

    const {multiSelectOptions} = props;
    const numTotalTableColumns = React.useMemo(
      () =>
        shownColumns.length +
        (tableHasAtLeastOneExpandableRow ? 1 : 0) +
        (multiSelectOptions && multiSelectOptions.popoverOptions && multiSelectOptions.popoverOptions.length > 0 ? 1 : 0) +
        1, // For the popover column
      [
        shownColumns,
        multiSelectOptions,
        tableHasAtLeastOneExpandableRow
      ]
    );

    const shownMultiSelectOptionsPopoverOptions = React.useMemo(
      () => _.filter(multiSelectOptions?.popoverOptions ?? [], option =>
        _.isUndefined(option.show) || option.show),
      [multiSelectOptions]
    );

    const showMultiSelectOptions = React.useMemo(
      () => multiSelectOptions &&
        shownMultiSelectOptionsPopoverOptions.length > 0 &&
        numRowsChecked > 0,
      [
        multiSelectOptions,
        shownMultiSelectOptionsPopoverOptions,
        numRowsChecked
      ]
    );

    const [sortOptions, setSortOptions] = React.useState<sortUtil.ISortOptions<TColumnId>>(props.initSortOptions ?? {
      columnId: shownColumns[0].id ?? columns[0].id,
      direction: 'ascending'
    });



    const setRowId2IsChecked = React.useCallback(
      (valueOrFn: Record<string, boolean> | ((previous: Record<string, boolean>) => Record<string, boolean>)) => {
        let value: Record<string, boolean> = {};
        if (_.isFunction(valueOrFn)) {
          value = valueOrFn(rowId2IsChecked);
        } else {
          value = valueOrFn;
        }

        _setRowId2IsChecked(value);
        multiSelectOptions?.onChangeSelections?.(value);
      },
      [
        rowId2IsChecked,
        multiSelectOptions
      ]
    );



    const handleSelectRow = React.useCallback(
      (rowId: string, value: boolean) => {
        if (multiSelectOptions?.singleSelect) {
          setRowId2IsChecked({
            [rowId]: value
          });

        } else {
          setRowId2IsChecked(previous => ({
            ...previous,
            [rowId]: value
          }));
        }
      },
      [
        multiSelectOptions,
        setRowId2IsChecked
      ]
    );



    React.useEffect(
      () => {
        const sub = props.multiSelectOptions?.obsDeselectAll?.subscribe(() => {
          setRowId2IsChecked({});
        });

        return () => sub?.unsubscribe();
      },
      [
        props.multiSelectOptions?.obsDeselectAll,
        setRowId2IsChecked
      ]
    );



    const tableStyle = props.tableStyle ?? options?.tableStyle;
    const tableBodyRowStyle = props.tableBodyRowStyle ?? options?.tableBodyRowStyle;
    const tableBodyCellStyle = props.tableBodyCellStyle ?? options?.tableBodyCellStyle;
    const tableHeaderRowStyle = props.tableHeaderRowStyle ?? options?.tableHeaderRowStyle;
    const tableHeaderCellStyle = props.tableHeaderCellStyle ?? options?.tableHeaderCellStyle;
    const cellTypographyStyle = props.cellTypographyStyle ?? options?.cellTypographyStyle;
    const headerCellTypographyStyle = props.headerCellTypographyStyle ?? options?.headerCellTypographyStyle;
    const Checkbox = props.fcCheckbox ?? options?.fcCheckbox ?? DefaultCheckbox;



    const handleClickSelectAll = React.useCallback(
      () => {
        if (areAllRowsSelected) {
          setRowId2IsChecked({});

        } else {
          setRowId2IsChecked(
            _.zipObject(
              _.map(props.rows, row => row.id),
              _.times(props.rows.length, () => true)
            ));
        }
      },
      [
        setRowId2IsChecked,
        areAllRowsSelected,
        props.rows
      ]
    );



    const wrappedSetSortOptions = React.useCallback(
      (fnUpdate: (previous: sortUtil.ISortOptions<TColumnId>) => sortUtil.ISortOptions<TColumnId>) => {
        setSortOptions(fnUpdate);
        props.onChangeSortOptions?.(fnUpdate(sortOptions));
      },
      [
        sortOptions,
        setSortOptions,
        props
      ]
    );



    const sortedRows = React.useMemo(
      () => {
        const sortByColumnId = sortOptions?.columnId ?? '';
        if (props.disableSorting || props.disableClientSorting || !sortByColumnId) {
          return props.rows;
        }

        let retVal = _.sortBy(props.rows, row => {
          const rowValue = row.columnId2Value[sortByColumnId];
          const sortableValue = rowValue.type === 'string' ?
            (rowValue.sortableValue ?? rowValue.label) :
            rowValue.sortableValue;

          return sortableValue;
        });

        if (sortOptions?.direction === 'descending') {
          retVal = _.reverse(retVal);
        }

        return retVal;
      },
      [
        props.rows,
        props.disableSorting,
        props.disableClientSorting,
        sortOptions.columnId,
        sortOptions.direction
      ]
    );



    function rowValue2Component(column: IColumn<TColumnId>, rowValue?: IRowValue) {
      const urlType = rowValue?.isExternalUrl ? 'external' : 'internal';

      if (!rowValue) {
        return (
          <div />
        );

      } else if (rowValue.type === 'component') {
        const displayAsRow = rowValue.type === 'component' ?
          !!rowValue.displayAsRow :
          false;

        const labelComponent = displayAsRow ? (
          <div
            style={{
              display: displayAsRow ? 'flex' : undefined,
              alignItems: displayAsRow ? 'center' : undefined,
              justifyContent: displayAsRow && column?.textAlign === 'right' ?
                'flex-end' :
                undefined
            }}>
            {rowValue.label}
          </div>
        ) : rowValue.label;

        if (rowValue.toRoute) {
          return __genLink(rowValue.toRoute, urlType, labelComponent);
        } else {
          return labelComponent;
        }

      } else if (rowValue.type === 'string') {
        const style: React.CSSProperties = {
          textAlign: column.textAlign,
          ...cellTypographyStyle
        };

        if (rowValue.toRoute) {
          return (
            <CellTypography
              style={style}>
              {__genLink(rowValue.toRoute, urlType, rowValue.label)}
            </CellTypography>
          );

        } else {
          return (
            <CellTypography
              style={style}>
              {rowValue.label}
            </CellTypography>
          );
        }

      } else {
        return (
          <div />
        );
      }
    }



    function handleClickColumnHeader(columnId: TColumnId) {
      if (sortOptions.columnId !== columnId) {
        wrappedSetSortOptions(() => ({
          columnId: columnId,
          direction: 'ascending'
        }));

      } else if (sortOptions.direction === 'ascending') {
        wrappedSetSortOptions(previous => ({
          ...previous,
          direction: 'descending'
        }));

      } else {
        wrappedSetSortOptions(previous => ({
          ...previous,
          columnId: ''
        }));
      }
    }



    const handlePageChange = React.useCallback(
      (page: number) => {
        setRowId2IsChecked({});
        props.paginationOptions?.onChangePage(page);
      },
      [
        props.paginationOptions,
        setRowId2IsChecked
      ]
    );



    const expandCollapseAllRows = React.useCallback(
      (isExpanded: boolean) => {
        if (isExpanded) {
          setRowId2IsExpanded(_.zipObject(
            _.map(props.rows, row => row.id),
            _.times(props.rows.length, () => true)
          ));

        } else {
          setRowId2IsExpanded({});
        }
      },
      [
        props,
        setRowId2IsExpanded
      ]
    );



    const elExpandCollapseAllRowsButtonCell = React.useMemo(
      () => (
        <ExpandedContentHeaderTableCell
          style={tableHeaderCellStyle}>
          <ExpandCollapseButton.FC
            isExpanded={_.every(props.rows, row => !!rowId2IsExpanded[row.id])}
            onChange={isExpanded => expandCollapseAllRows(isExpanded)} />
        </ExpandedContentHeaderTableCell>
      ),
      [
        props,
        expandCollapseAllRows,
        rowId2IsExpanded,
        tableHeaderCellStyle
      ]
    );



    const elSortAscendingIcon = options?.elSortAscendingIcon ?? (
      <ArrowDropUp fontSize="small" />
    );

    const elSortDescendingIcon = options?.elSortDescendingIcon ?? (
      <ArrowDropDown fontSize="small" />
    );



    return (
      <MuiTable
        size="small"
        style={tableStyle}>
        <TableHead>
          <TableRow
            style={tableHeaderRowStyle}>
            {props.multiSelectOptions && !props.multiSelectOptions.singleSelect && (shownMultiSelectOptionsPopoverOptions.length > 0 || props.multiSelectOptions.onChangeSelections) && (
              <SelectAllHeaderTableCell
                variant="head">
                <HeaderCellBox>
                  <Checkbox
                    isChecked={areAllRowsSelected}
                    onChange={handleClickSelectAll} />
                </HeaderCellBox>
              </SelectAllHeaderTableCell>
            )}

            {tableHasAtLeastOneExpandableRow && expandButtonPosition === 'left' &&
              elExpandCollapseAllRowsButtonCell}

            {_.map(shownColumns, column => (
              <TableCell
                key={column.id}
                variant="head"
                style={{
                  ...tableHeaderCellStyle,
                  cursor: !props.disableSorting && !column.disableSorting ? 'pointer' : undefined,
                  width: column.width
                }}
                onClick={() => !props.disableSorting && !column.disableSorting ?
                  handleClickColumnHeader(column.id) :
                  undefined}>

                <HeaderCellBox
                  align={column.textAlign}>
                  <HeaderCellTypography
                    style={headerCellTypographyStyle}>
                    {column.label}
                  </HeaderCellTypography>

                  <HiddenBox
                    isHidden={props.disableSorting || column.disableSorting || sortOptions.columnId !== column.id}>

                    {sortOptions.direction === 'ascending' && (
                      <IconContainerBox>
                        {elSortAscendingIcon}
                      </IconContainerBox>
                    )}

                    {sortOptions.direction === 'descending' && (
                      <IconContainerBox>
                        {elSortDescendingIcon}
                      </IconContainerBox>
                    )}

                  </HiddenBox>
                </HeaderCellBox>

              </TableCell>
            ))}

            {tableHasAtLeastOneExpandableRow && expandButtonPosition === 'right' &&
              elExpandCollapseAllRowsButtonCell}

            {tableHasRowPopoverColumn && (
              <PopoverHeaderTableCell
                variant="head"
                size="small"
                style={tableHeaderCellStyle} />
            )}
          </TableRow>
        </TableHead>

        <TableBody>
          {_.map(sortedRows, row => {
            const elExpandCollapseButtonCell = (
              <TableCell>
                {!!row.expandedContent && (
                  <ExpandCollapseButton.FC
                    isExpanded={!!rowId2IsExpanded[row.id]}
                    onChange={isExpanded => setRowId2IsExpanded(previous => ({
                      ...previous,
                      [row.id]: isExpanded
                    }))} />
                )}
              </TableCell>
            );

            const shownPopoverOptions = _.filter(row.popoverOptions ?? [], option =>
              _.isUndefined(option.show) || option.show);

            return (
              <React.Fragment key={row.id}>
                <TableRow
                  backgroundColor={row.backgroundColor}
                  style={tableBodyRowStyle}>
                  {props.multiSelectOptions && (shownMultiSelectOptionsPopoverOptions.length > 0 || props.multiSelectOptions.onChangeSelections) && (
                    <SelectTableCell
                      style={tableBodyCellStyle}>
                      <Checkbox
                        isChecked={!!rowId2IsChecked[row.id]}
                        onChange={value => handleSelectRow(row.id, value)} />
                    </SelectTableCell>
                  )}

                  {tableHasAtLeastOneExpandableRow && expandButtonPosition === 'left' &&
                    elExpandCollapseButtonCell}

                  {_.map(shownColumns, column => {
                    const rowValue = row.columnId2Value[column.id];

                    return (
                      <TableCell
                        key={column.id}
                        style={{
                          ...tableBodyCellStyle,
                          width: column.width}}>
                        {rowValue2Component(column, rowValue)}
                      </TableCell>
                    );
                  })}

                  {tableHasAtLeastOneExpandableRow && expandButtonPosition === 'right' &&
                    elExpandCollapseButtonCell}

                  {tableHasRowPopoverColumn && (
                    <PopoverTableCell
                      size="small"
                      style={tableBodyCellStyle}>
                      {row.popoverOptions && shownPopoverOptions.length > 0 && (
                        <RowPopover.FC
                          options={row.popoverOptions}
                          onClickOption={optionId => props.onClickRowPopoverOption?.(row.id, optionId)} />
                      )}
                    </PopoverTableCell>
                  )}
                </TableRow>

                {tableHasAtLeastOneExpandableRow && (
                  <TableRow>
                    <ExpandedContentTableCell
                      colSpan={numTotalTableColumns}>
                      <Collapse
                        in={rowId2IsExpanded[row.id]}
                        timeout="auto"
                        unmountOnExit>
                        {row.expandedContent}
                      </Collapse>
                    </ExpandedContentTableCell>
                  </TableRow>
                )}
              </React.Fragment>
            );
          })}
        </TableBody>

        {(props.paginationOptions || showMultiSelectOptions) && (
          <TableFooter>
            <TableRow>
              {showMultiSelectOptions && (
                <SelectionFooterTableCell>
                  <RowBox.FC>
                    <Typography>
                      {numRowsChecked} row(s) selected
                    </Typography>

                    {shownMultiSelectOptionsPopoverOptions.length > 0 && (
                      <RowPopover.FC
                        options={props.multiSelectOptions?.popoverOptions ?? []}
                        onClickOption={optionId =>
                          props.multiSelectOptions?.onClickPopoverOption?.(
                            _.keys(_.pickBy(rowId2IsChecked)),
                            optionId
                          )}
                        isInFooter />
                    )}
                  </RowBox.FC>
                </SelectionFooterTableCell>
              )}

              {props.paginationOptions && (
                <TablePagination
                  count={props.paginationOptions.rowCount}
                  rowsPerPage={props.paginationOptions.rowsPerPage}
                  rowsPerPageOptions={[]}
                  page={props.paginationOptions.page}
                  onPageChange={(_event, page) => handlePageChange(page)}
                  showFirstButton
                  showLastButton />
              )}
            </TableRow>
          </TableFooter>
        )}
      </MuiTable>
    );
  };

  return Table;
}



type IUrlType =
  'internal' |
  'external';

function __genLink(url: string, urlType: IUrlType, content: string | JSX.Element) {
  return urlType === 'internal' ? (
    <Link
      component={RouterLink}
      to={url}>
      {content}
    </Link>

  ) : (
    <Link
      component="a"
      href={url}>
      {content}
    </Link>
  );
}



const DefaultCheckbox: React.FC<ICheckboxProps> = props => {
  return (
    <MuiCheckbox
      checked={props.isChecked}
      value={props.isChecked}
      onChange={(event, value) => props.onChange(value)} />
  );
};
